/**
 * FeedbackRateLimiter — Client-side abuse protection for GitHub issue creation.
 *
 * Enforces a cooldown between issue submission events.
 *
 * Rate limit counters in localStorage are protected by an HMAC to detect
 * tampering. The HMAC uses a static application salt — this is deterrent-level
 * protection, not cryptographic security. Server-side rate limiting in the
 * Cloud Functions provides the authoritative enforcement.
 */

import type { RateLimitState } from "../types/feedback";
import { createLogger } from "../utils/logger";

const rateLimitLogger = createLogger({ prefix: "FeedbackRateLimiter" });
const STORAGE_KEY = "feedbackRateLimits";

// Rate limit configuration
const SUBMISSION_COOLDOWN_SECONDS = 15;
const SUBMISSION_COOLDOWN_MS = SUBMISSION_COOLDOWN_SECONDS * 1000;
const WINDOW_MS = 60 * 60 * 1000; // 1 hour

// Static salt for HMAC (deterrent only — authoritative enforcement is server-side)
const HMAC_SALT = "retireez-feedback-rate-limiter-v1";

export class FeedbackRateLimiter {
  constructor() {
    // No constructor arguments needed — the HMAC salt is static
  }

  /** Check if a batch of N issues can be created */
  canCreateIssues(count: number): {
    allowed: boolean;
    reason?: string;
    cooldownSeconds?: number;
  } {
    const state = this.loadState();

    // Count is intentionally unconstrained: a single submit event may contain
    // any number of issues. Only submit frequency is rate-limited.
    void count;

    // Prune old timestamps
    const now = Date.now();
    state.issueTimestamps = state.issueTimestamps.filter(
      (ts) => now - new Date(ts).getTime() < WINDOW_MS
    );
    state.sessionTimestamps = state.sessionTimestamps.filter(
      (ts) => now - new Date(ts).getTime() < WINDOW_MS
    );
    state.cooldownUntil = null;

    const latestSessionTimestamp = state.sessionTimestamps.at(-1);
    if (latestSessionTimestamp) {
      const elapsedMs = now - new Date(latestSessionTimestamp).getTime();
      if (elapsedMs < SUBMISSION_COOLDOWN_MS) {
        const remainingSeconds = Math.ceil(
          (SUBMISSION_COOLDOWN_MS - elapsedMs) / 1000
        );
        this.saveState(state);
        return {
          allowed: false,
          reason: `Rate limit cooldown active. Try again in ${remainingSeconds} second(s).`,
          cooldownSeconds: remainingSeconds,
        };
      }
    }

    this.saveState(state);
    return { allowed: true };
  }

  /** Record that issues were created */
  recordIssuesCreated(count: number): void {
    if (count <= 0) return;

    const state = this.loadState();
    const now = new Date().toISOString();

    for (let i = 0; i < count; i++) {
      state.issueTimestamps.push(now);
    }
    state.sessionTimestamps.push(now);

    this.saveState(state);
  }

  /** Get current rate limit status for display */
  getStatus(): {
    issuesUsed: number;
    issuesRemaining: number;
    sessionsUsed: number;
    sessionsRemaining: number;
    inCooldown: boolean;
  } {
    const state = this.loadState();
    const now = Date.now();

    const issuesUsed = state.issueTimestamps.filter(
      (ts) => now - new Date(ts).getTime() < WINDOW_MS
    ).length;

    const sessionsUsed = state.sessionTimestamps.filter(
      (ts) => now - new Date(ts).getTime() < WINDOW_MS
    ).length;

    const latestSessionTimestamp = state.sessionTimestamps.at(-1);
    const inCooldown =
      !!latestSessionTimestamp &&
      now - new Date(latestSessionTimestamp).getTime() < SUBMISSION_COOLDOWN_MS;

    return {
      issuesUsed,
      issuesRemaining: Number.MAX_SAFE_INTEGER,
      sessionsUsed,
      sessionsRemaining: Number.MAX_SAFE_INTEGER,
      inCooldown,
    };
  }

  private loadState(): RateLimitState {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return this.emptyState();

      const parsed = JSON.parse(raw) as RateLimitState;

      // Verify HMAC
      const expectedHmac = this.computeHmac(parsed);
      if (parsed.hmac !== expectedHmac) {
        // HMAC mismatch — could be tampering, code update, or data drift.
        // Reset to empty state without cooldown; server-side enforcement
        // is authoritative so false positives here should not block users.
        rateLimitLogger.debug("HMAC mismatch — resetting rate limit state");
        const resetState = this.emptyState();
        this.saveState(resetState);
        return resetState;
      }

      return parsed;
    } catch (error) {
      rateLimitLogger.debug("localStorage unavailable for rate limit state", {
        error,
      });
      return this.emptyState();
    }
  }

  private saveState(state: RateLimitState): void {
    state.hmac = this.computeHmac(state);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      rateLimitLogger.debug("localStorage unavailable for rate limit save", {
        error,
      });
    }
  }

  private emptyState(): RateLimitState {
    return {
      issueTimestamps: [],
      sessionTimestamps: [],
      cooldownUntil: null,
      hmac: "",
    };
  }

  /**
   * Compute a simple hash-based MAC for tamper detection.
   * Uses a synchronous string-based approach (not crypto.subtle)
   * since this is deterrent-level, not cryptographic security.
   */
  private computeHmac(state: RateLimitState): string {
    const payload = JSON.stringify({
      issueTimestamps: state.issueTimestamps,
      sessionTimestamps: state.sessionTimestamps,
      cooldownUntil: state.cooldownUntil,
    });

    // Simple hash: FNV-1a variant keyed with static salt
    const input = payload + "|" + HMAC_SALT;
    let hash = 0x811c9dc5; // FNV offset basis
    for (let i = 0; i < input.length; i++) {
      hash ^= input.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193); // FNV prime
    }
    return (hash >>> 0).toString(36);
  }
}
